import moment from 'moment-timezone';

import { ALERT_DEFINITION_GET_RESULT, actions } from '../../../pages/SuperUser/Patients/Dashboard/actions';
import { actions as patientActions } from '../../../pages/SuperUser/Patients/redux/actions';
import { actions as scheduleActions } from '../../../pages/SuperUser/Schedules/redux/actions';
import { GET_CAP_PLOT_DATA_RESULT } from '../../../pages/SuperUser/Schedules/redux/constants';
import {
  actions as plotActions,
  PLOT_DATA_RESULT,
  PLOT_DATE_RANGE_RESULT,
} from '../../../pages/SuperUser/DeviceManagement/HubDeviceEvents/Graph/actions';
import { GET_ANNOTATIONS_RESULT, GET_SCHEDULE_NOTIFICATIONS_RESULT } from '../../../pages/SuperUser/Patients/redux/constants';
import { store } from '../../../utils/store';
import {
  GLUCOSE_UNITS,
  GlucoseUnitConverter,
  TEMPERATURE_UNITS,
  WEIGHT_UNITS,
  convertCelsiusToFahrenheit,
  convertKilogramToPound,
  roundToDecimal,
} from '../../../utils/unitConverters';
import { getMetricsByReadings, graphMetricEnum, graphMetrics } from './Metrics';
import { prepareSimplifiedDataForDataProvider } from './simplifyData';
import { getHeartRateReceipt } from './heartRateReceipt';
import { PERMISSIONS, hasPermission } from '../../../utils/userPermissions';
import { HR_DISTRIBUTON_RESOLUTION } from './Constants';
import { DATE_TIME_WITH_MILISECONDS } from '../../../constants';
import { VITALS } from '../../../utils/readings';
import { graphDataProvidersEnum } from './graphDataProvidersEnum';
import { seriesKpis, seriesToChartItems } from './chartSeries';

export { graphDataProvidersEnum };

const getRequestDataType = dp => {
  switch (dp) {
    case graphDataProvidersEnum.weight:
      return 'weight';
    case graphDataProvidersEnum.bp:
      return 'blood_pressure';
    case graphDataProvidersEnum.hr:
      return 'heart_rate';
    case graphDataProvidersEnum.sleep:
      return 'sleep_report';
    case graphDataProvidersEnum.pulseox:
      return 'pulseox';
    case graphDataProvidersEnum.gluco:
      return 'glucometer';
    case graphDataProvidersEnum.temp:
      return 'thermometer';
    case graphDataProvidersEnum.ptInr:
      return 'pt_inr';
    case graphDataProvidersEnum.airQualityMonitor:
      return 'atmocube';
    case graphDataProvidersEnum.spirometer:
      return 'spirometer';
    default:
      return '';
  }
};
// Chart data of a patient's vital as the chart code expects it. Falls back to the device-oriented endpoint for
// charts of one device/hub/kit and for charts that are not drawn from vitals.
const getPlotResponse = async (dp, request) => {
  const isPatientVitalChart = request.patientId && !request.deviceId && !request.hubId && !request.kitId && seriesKpis[dp];
  if (!isPatientVitalChart) {
    return store?.dispatch(plotActions.getPlotData(request));
  }
  const resp = await store?.dispatch(
    plotActions.getVitalSeries({
      patientId: request.patientId,
      kpis: seriesKpis[dp],
      startDate: request.startDate,
      endDate: request.endDate,
    }),
  );
  if (resp.type !== PLOT_DATA_RESULT) {
    return resp;
  }
  return { ...resp, response: { data: seriesToChartItems[dp](resp.response.data), goals: resp.response.goals } };
};

export class DataProviders {
  static async getTakes(patientId, med, dtStart, dtEnd) {
    const request = {
      startDate: dtStart.format(),
      endDate: dtEnd.format(),
      patientId,
    };

    const resp = await store?.dispatch(scheduleActions.getCapPlotData(patientId, med.rootId, request));
    const respNotif = await store?.dispatch(patientActions.getScheduleNotifications(med.rootId, request));

    if (resp.type === GET_CAP_PLOT_DATA_RESULT && resp.response.length !== 0 && resp.response.events) {
      let data = resp.response.events.map(i => {
        return {
          dt: i.type === 'missed_take' ? moment(i.scheduled * 1000) : moment(i.timestamp * 1000),
          value: i.type === 'scheduled_take' ? (i.timestamp - i.scheduled) / 60 / 60 : 0,
          type: i.type,
        };
      });
      if (respNotif.type === GET_SCHEDULE_NOTIFICATIONS_RESULT && respNotif.response?.reminders?.length > 0) {
        data = data.concat(
          respNotif.response.reminders.map(i => {
            return {
              dt: moment(i.sent_at),
              value: 0,
              type: i.type,
            };
          }),
        );
      }

      return data.sort((a, b) => a.dt.unix() - b.dt.unix());
    }

    return [];
  }

  static async getData(dp, patientId, deviceId, hubId, kitId, dtStart, dtEnd) {
    const ret = {};
    const request = {
      startDate: dtStart.format(DATE_TIME_WITH_MILISECONDS),
      endDate: dtEnd.format(DATE_TIME_WITH_MILISECONDS),
      dataType: getRequestDataType(dp),
    };
    if (patientId) request.patientId = patientId;
    if (deviceId) request.deviceId = deviceId;
    if (hubId) request.hubId = hubId;
    if (kitId) request.kitId = kitId;

    const getThresholds = hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_ALERT_TRESHOLDS) && patientId;
    let resp;

    switch (dp) {
      case graphDataProvidersEnum.weight:
        {
          resp = await getPlotResponse(dp, request);

          let thresholdWeightMin;
          let thresholdWeightMax;
          if (getThresholds) {
            const respAlertDef = await store?.dispatch(actions.getAlertDefinition(patientId, 'weight'));
            if (respAlertDef.type === ALERT_DEFINITION_GET_RESULT && respAlertDef.response) {
              const thresholdWeightMinKg = respAlertDef.response.conditions?.find(c => c.field === VITALS.WEIGHT)?.min;
              thresholdWeightMin = thresholdWeightMinKg ? roundToDecimal(convertKilogramToPound(thresholdWeightMinKg), 2) : null;
              const thresholdWeightMaxKg = respAlertDef.response.conditions?.find(c => c.field === VITALS.WEIGHT)?.max;
              thresholdWeightMax = thresholdWeightMaxKg ? roundToDecimal(convertKilogramToPound(thresholdWeightMaxKg), 2) : null;
            }
          }
          if (resp.type === PLOT_DATA_RESULT && resp.response.data.length !== 0) {
            const data = resp.response.data
              .map(i => {
                return {
                  dt: moment(i.event_timestamp),
                backend_timestamp: i.backend_timestamp,
                  value:
                    i.unit === WEIGHT_UNITS.kilogram ? roundToDecimal(convertKilogramToPound(i.weight), 2) : i.weight,
                };
              })
              .filter(d => !isNaN(d.value));
            const goals = resp.response.goals?.map(g => ({
                from: g.valid_from ? moment(g.valid_from) : dtStart,
                to: g.valid_until ? moment(g.valid_until) : dtEnd.clone().add(1, 'day').endOf('day'),
                min: g.min ? roundToDecimal(convertKilogramToPound(g.min), 2) : thresholdWeightMin,
                max: g.max ? roundToDecimal(convertKilogramToPound(g.max), 2) : thresholdWeightMax,
              })
            ) || [];

            ret[graphMetricEnum.weight] = {
              data,
              thresholdMin: thresholdWeightMin,
              thresholdMax: thresholdWeightMax,
              badData: v => v > thresholdWeightMax || v < thresholdWeightMin,
              goodData: (v, dt) => {
                const goal = goals.find(g => dt.isBetween(g.from, g.to, null, '[]'));
                return goal ? (v >= goal.min || !goal.min) && (v <= goal.max || !goal.max) : undefined;
              },
              goals,
            };

            const dataGroupped = _.groupBy(data, d =>
              d.dt
                .clone()
                .startOf('day')
                .unix(),
            );
            const dataDaily = Object.keys(dataGroupped).map(d => {
              return {
                dt: moment(d * 1000).add(12, 'hours'),
                value: roundToDecimal(
                  _.meanBy(dataGroupped[d], dd => dd.value),
                  2,
                ),
              };
            });

            ret[graphMetricEnum.weightDailyAvg] = {
              data: dataDaily,
              thresholdMin: thresholdWeightMin,
              thresholdMax: thresholdWeightMax,
              badData: v => v > thresholdWeightMax || v < thresholdWeightMin,
            };
          }
        }
        break;
      case graphDataProvidersEnum.bp:
        {
          resp = await getPlotResponse(dp, request);

          let thresholdSPMin, thresholdSPMax, thresholdDPMin, thresholdDPMax;
          if (getThresholds) {
            const respAlertDef = await store?.dispatch(actions.getAlertDefinition(patientId, 'blood_pressure'));
            if (respAlertDef.type === ALERT_DEFINITION_GET_RESULT && respAlertDef.response) {
              thresholdSPMin = respAlertDef.response.conditions?.find(c => c.field === VITALS.PRESSURE_SYSTOLIC)?.min;
              thresholdSPMax = respAlertDef.response.conditions?.find(c => c.field === VITALS.PRESSURE_SYSTOLIC)?.max;
              thresholdDPMin = respAlertDef.response.conditions?.find(c => c.field === VITALS.PRESSURE_DIASTOLIC)?.min;
              thresholdDPMax = respAlertDef.response.conditions?.find(c => c.field === VITALS.PRESSURE_DIASTOLIC)?.max;
            }
          }

          if (resp.type === PLOT_DATA_RESULT && resp.response.data.length !== 0) {
            const dataSP = resp.response.data.map(i => {
              return {
                dt: moment(i.event_timestamp),
                backend_timestamp: i.backend_timestamp,
                value: i.systolic_pressure,
              };
            });
            const goalsSP = resp.response.goals?.filter(g => g.field === VITALS.PRESSURE_SYSTOLIC)?.map(g => ({
                from: g.valid_from ? moment(g.valid_from) : dtStart,
                to: g.valid_until ? moment(g.valid_until) : dtEnd.clone().add(1, 'day').endOf('day'),
                min: g.min || thresholdSPMin,
                max: g.max || thresholdSPMax,
              })
            ) || [];
            ret[graphMetricEnum.bpSP] = {
              data: dataSP.filter(d => !isNaN(d.value)),
              thresholdMin: thresholdSPMin,
              thresholdMax: thresholdSPMax,
              badData: v => v > thresholdSPMax || v < thresholdSPMin,
              goodData: (v, dt) => {
                const goal = goalsSP.find(g => dt.isBetween(g.from, g.to, null, '[]'));
                return goal ? (v >= goal.min || !goal.min) && (v <= goal.max || !goal.max) : undefined;
              },
              goals: goalsSP,
            };

            const dataPP = resp.response.data.map(i => {
              return {
                dt: moment(i.event_timestamp),
                backend_timestamp: i.backend_timestamp,
                value: i.mean_arterial_pressure,
              };
            });
            ret[graphMetricEnum.bpPP] = { data: dataPP.filter(d => !isNaN(d.value)) };

            const dataDP = resp.response.data.map(i => {
              return {
                dt: moment(i.event_timestamp),
                backend_timestamp: i.backend_timestamp,
                value: i.diastolic_pressure,
              };
            });
            const goalsDP = resp.response.goals?.filter(g => g.field === VITALS.PRESSURE_DIASTOLIC)?.map(g => ({
                from: g.valid_from ? moment(g.valid_from) : dtStart,
                to: g.valid_until ? moment(g.valid_until) : dtEnd.clone().add(1, 'day').endOf('day'),
                min: g.min || thresholdDPMin,
                max: g.max || thresholdDPMax,
              })
            ) || [];
            ret[graphMetricEnum.bpDP] = {
              data: dataDP.filter(d => !isNaN(d.value)),
              thresholdMin: thresholdDPMin,
              thresholdMax: thresholdDPMax,
              badData: v => v > thresholdDPMax || v < thresholdDPMin,
              goodData: (v, dt) => {
                const goal = goalsDP.find(g => dt.isBetween(g.from, g.to, null, '[]'));
                return goal ? (v >= goal.min || !goal.min) && (v <= goal.max || !goal.max) : undefined;
              },
              goals: goalsDP,
            };

            const dataPR = resp.response.data.map(i => {
              return {
                dt: moment(i.event_timestamp),
                backend_timestamp: i.backend_timestamp,
                value: i.pulse_rate,
              };
            });
            ret[graphMetricEnum.bpPR] = { data: dataPR.filter(d => !isNaN(d.value)) };
          }
        }
        break;
      case graphDataProvidersEnum.hr:
        resp = await getPlotResponse(dp, request);

        let thresholdHRMin, thresholdHRMax;
        if (getThresholds) {
          const respAlertDef = await store?.dispatch(actions.getAlertDefinition(patientId, 'heart_rate'));
          if (respAlertDef.type === ALERT_DEFINITION_GET_RESULT && respAlertDef.response) {
            thresholdHRMin = respAlertDef.response.conditions?.find(c => c.field === VITALS.HEART_RATE)?.min;
            thresholdHRMax = respAlertDef.response.conditions?.find(c => c.field === VITALS.HEART_RATE)?.max;
          }
        }

        if (resp.type === PLOT_DATA_RESULT && resp.response.data.length !== 0) {
          {
            const data = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.heart_rate,
              };
            });

            const goals = resp.response.goals?.map(g => ({
                from: g.valid_from ? moment(g.valid_from) : dtStart,
                to: g.valid_until ? moment(g.valid_until) : dtEnd.clone().add(1, 'day').endOf('day'),
                min: g.min || thresholdHRMin,
                max: g.max || thresholdHRMax,
              })
            ) || [];

            ret[graphMetricEnum.hr] = {
              data: prepareSimplifiedDataForDataProvider(data),
              thresholdMin: thresholdHRMin,
              thresholdMax: thresholdHRMax,
              badData: v => v > thresholdHRMax || v < thresholdHRMin,
              goodData: (v, dt) => {
                const goal = goals.find(g => dt.isBetween(g.from, g.to, null, '[]'));
                return goal ? (v >= goal.min || !goal.min) && (v <= goal.max || !goal.max) : undefined;
              },
              goals,
            };
          }

          {
            const dailyHrs = [];
            resp.response.data.forEach(i => {
              const day = moment(i.timestamp * 1000).startOf('day');
              const dailyHr = dailyHrs.find(i => i.dt.unix() === day.unix());
              if (dailyHr) {
                dailyHr.value += i.heart_rate;
                dailyHr.count++;
                Object.assign(dailyHr, getHeartRateReceipt(dailyHr, i, 'day'));
              } else
                dailyHrs.push({
                  dt: day,
                  value: i.heart_rate,
                  count: 1,
                  ...getHeartRateReceipt({}, i, 'day'),
                });
            });

            dailyHrs.forEach(i => {
              i.value = roundToDecimal(i.value / i.count, 2);
            });

            ret[graphMetricEnum.hrDailyAvg] = { data: dailyHrs.filter(d => !isNaN(d.value)) };
          }

          {
            const dailyHrs = [];
            resp.response.data.forEach(i => {
              const day = moment(i.timestamp * 1000).startOf('hour');
              const dailyHr = dailyHrs.find(i => i.dt.unix() === day.unix());
              if (dailyHr) {
                dailyHr.value += i.heart_rate;
                dailyHr.count++;
                Object.assign(dailyHr, getHeartRateReceipt(dailyHr, i, 'hour'));
              } else
                dailyHrs.push({
                  dt: day,
                  value: i.heart_rate,
                  count: 1,
                  ...getHeartRateReceipt({}, i, 'hour'),
                });
            });

            dailyHrs.forEach(i => {
              i.value = roundToDecimal(i.value / i.count, 2);
            });

            ret[graphMetricEnum.hrHourlyAvg] = { data: dailyHrs.filter(d => !isNaN(d.value)) };
          }

          {
            const dailyHrs = [];
            const resolution = HR_DISTRIBUTON_RESOLUTION;
            resp.response.data.forEach(i => {
              const day = moment(i.timestamp * 1000)
                .startOf('day')
                .add(12, 'hours');
              let dailyHr = dailyHrs.find(i => i.dt.unix() === day.unix());
              const bucket = Math.floor(i.heart_rate / resolution);
              if (!dailyHr) {
                dailyHr = {
                  dt: day,
                  value: bucket * resolution,
                  statistics: {
                    values: [],
                    minValue: i.heart_rate,
                    maxValue: i.heart_rate,
                    count: 0,
                  },
                };
                dailyHrs.push(dailyHr);
              }

              Object.assign(dailyHr, getHeartRateReceipt(dailyHr, i, 'day'));

              const tenthElement = dailyHr.statistics.values.find(i => i.bucket === bucket);
              if (tenthElement) {
                tenthElement.count++;
              } else {
                dailyHr.statistics.values.push({
                  bucket,
                  count: 1,
                });
              }

              dailyHr.statistics.count++;
              if (i.heart_rate < dailyHr.statistics.minValue) dailyHr.statistics.minValue = i.heart_rate;
              if (i.heart_rate > dailyHr.statistics.maxValue) dailyHr.statistics.maxValue = i.heart_rate;
              dailyHr.statistics.width = dailyHr.statistics.maxValue - dailyHr.statistics.minValue;
            });

            ret[graphMetricEnum.hrDailyDistribution] = {
              data: dailyHrs,
            };

            ret[graphMetricEnum.hrDailyBar] = {
              data: dailyHrs,
            };
          }
        }
        break;
      case graphDataProvidersEnum.sleep: {
        resp = await getPlotResponse(dp, request);
        if (resp.type === PLOT_DATA_RESULT && resp.response.length !== 0) {
          const sorted = resp.response.data.sort((a, b) => a.sleep_start - b.sleep_start);

          const dataSD = sorted.map(i => {
            return {
              dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
              value: roundToDecimal((i.sleep_stop - i.sleep_start) / 60 / 60, 1),
            };
          });
          ret[graphMetricEnum.sleepDuration] = { data: dataSD.filter(d => !isNaN(d.value)) };

          const dataA = sorted.map(i => {
            return {
              dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
              value: i.awake_duration,
            };
          });
          ret[graphMetricEnum.awakeDuration] = { data: dataA.filter(d => !isNaN(d.value)) };

          const dataDS = sorted.map(i => {
            return {
              dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
              value: i.deep_sleep_duration,
            };
          });
          ret[graphMetricEnum.deepSleepDuration] = { data: dataDS.filter(d => !isNaN(d.value)) };

          const dataLS = sorted.map(i => {
            return {
              dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
              value: i.light_sleep_duration,
            };
          });
          ret[graphMetricEnum.lightSleepDuration] = { data: dataLS.filter(d => !isNaN(d.value)) };

          const dataRem = sorted.map(i => {
            return {
              dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
              value: i.rem_duration,
            };
          });
          ret[graphMetricEnum.remDuration] = { data: dataRem.filter(d => !isNaN(d.value)) };
        }
        break;
      }
      case graphDataProvidersEnum.pulseox:
        {
          resp = await getPlotResponse(dp, request);

          let thresholdSatMin;
          if (getThresholds) {
            const respAlertDef = await store?.dispatch(actions.getAlertDefinition(patientId, 'saturation'));
            if (respAlertDef.type === ALERT_DEFINITION_GET_RESULT && respAlertDef.response) {
              thresholdSatMin = respAlertDef.response.conditions?.find(c => c.field === VITALS.OXYGEN_SATURATION)?.min;
            }
          }

          if (resp.type === PLOT_DATA_RESULT && resp.response.data.length !== 0) {
            const dataS = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.saturation,
              };
            });
            const goals = resp.response.goals?.map(g => ({
              from: g.valid_from ? moment(g.valid_from) : dtStart,
              to: g.valid_until ? moment(g.valid_until) : dtEnd.clone().add(1, 'day').endOf('day'),
              min: g.min || thresholdSatMin,
              max: g.max || 100,
            })) || [];
            ret[graphMetricEnum.saturation] = {
              data: dataS.filter(d => !isNaN(d.value)),
              thresholdMin: thresholdSatMin,
              badData: v => v < thresholdSatMin,
              goodData: (v, dt) => {
                const goal = goals.find(g => dt.isBetween(g.from, g.to, null, '[]'));
                return goal ? (v >= goal.min || !goal.min) && (v <= goal.max || !goal.max) : undefined;
              },
              goals,
            };

            const dataPi = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.pi,
              };
            });
            ret[graphMetricEnum.pi] = { data: dataPi.filter(d => !isNaN(d.value)) };
          }
        }
        break;
      case graphDataProvidersEnum.gluco:
        {
          resp = await getPlotResponse(dp, request);

          let thresholdGlucoMin, thresholdGlucoMax;
          if (getThresholds) {
            const respAlertDef = await store?.dispatch(actions.getAlertDefinition(patientId, 'glucose'));
            if (respAlertDef.type === ALERT_DEFINITION_GET_RESULT && respAlertDef.response) {
              thresholdGlucoMin = respAlertDef.response.conditions?.find(c => c.field === VITALS.SUGAR_LEVEL)?.min;
              thresholdGlucoMax = respAlertDef.response.conditions?.find(c => c.field === VITALS.SUGAR_LEVEL)?.max;
            }
          }

          if (resp.type === PLOT_DATA_RESULT && resp.response.data.length !== 0) {
            const data = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp),
                backend_timestamp: i.backend_timestamp,
                value: roundToDecimal(
                  GlucoseUnitConverter(i.glucose_concentration, i.glucose_unit, GLUCOSE_UNITS.mg_dl),
                  2,
                ),
              };
            });
            const goals = resp.response.goals?.map(g => ({
                from: g.valid_from ? moment(g.valid_from) : dtStart,
                to: g.valid_until ? moment(g.valid_until) : dtEnd.clone().add(1, 'day').endOf('day'),
                min: g.min
                  ? roundToDecimal(GlucoseUnitConverter(g.min, g.glucose_unit, GLUCOSE_UNITS.mg_dl), 2)
                  : thresholdGlucoMin,
                max: g.max
                  ? roundToDecimal(GlucoseUnitConverter(g.max, g.glucose_unit, GLUCOSE_UNITS.mg_dl), 2)
                  : thresholdGlucoMax,
              })
            ) || [];
            ret[graphMetricEnum.glucose] = {
              data: data.filter(d => !isNaN(d.value)),
              thresholdMin: thresholdGlucoMin,
              thresholdMax: thresholdGlucoMax,
              badData: v => v > thresholdGlucoMax || v < thresholdGlucoMin,
              goodData: (v, dt) => {
                const goal = goals.find(g => dt.isBetween(g.from, g.to, null, '[]'));
                return goal ? (v >= goal.min || !goal.min) && (v <= goal.max || !goal.max) : undefined;
              },
              goals,
            };
          }
        }
        break;
      case graphDataProvidersEnum.temp:
        {
          resp = await getPlotResponse(dp, request);

          let thresholdTempMin, thresholdTempMax;
          if (getThresholds) {
            const respAlertDef = await store?.dispatch(actions.getAlertDefinition(patientId, 'temperature'));
            if (respAlertDef.type === ALERT_DEFINITION_GET_RESULT && respAlertDef.response) {
              thresholdTempMin = roundToDecimal(
                convertCelsiusToFahrenheit(respAlertDef.response.conditions?.find(c => c.field === VITALS.TEMPERATURE)?.min),
                2,
              );
              thresholdTempMax = roundToDecimal(
                convertCelsiusToFahrenheit(respAlertDef.response.conditions?.find(c => c.field === VITALS.TEMPERATURE)?.max),
                2,
              );
            }
          }

          if (resp.type === PLOT_DATA_RESULT && resp.response.data.length !== 0) {
            const data = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value:
                  i.unit === TEMPERATURE_UNITS.c || !i.unit
                    ? roundToDecimal(convertCelsiusToFahrenheit(i.temperature), 2)
                    : i.temperature,
              };
            });
            const goals = resp.response.goals?.map(g => ({
                from: g.valid_from ? moment(g.valid_from) : dtStart,
                to: g.valid_until ? moment(g.valid_until) : dtEnd.clone().add(1, 'day').endOf('day'),
                min: g.min ? roundToDecimal(convertCelsiusToFahrenheit(g.min), 2) : thresholdTempMin,
                max: g.max ? roundToDecimal(convertCelsiusToFahrenheit(g.max), 2) : thresholdTempMax,
              })
            ) || [];
            ret[graphMetricEnum.temperature] = {
              data: data.filter(d => !isNaN(d.value)),
              thresholdMin: thresholdTempMin,
              thresholdMax: thresholdTempMax,
              badData: v => v > thresholdTempMax || v < thresholdTempMin,
              goodData: (v, dt) => {
                if (!goals.length) return null;
                const goal = goals.find(g => dt.isBetween(g.from, g.to, null, '[]'));
                return goal ? (v >= goal.min || !goal.min) && (v <= goal.max || !goal.max) : undefined;
              },
              goals,
            };
          }
        }
        break;
      //       ? 'steps_report'
      //       ? 'movement_report'
      case graphDataProvidersEnum.airQualityMonitor:
        {
          resp = await getPlotResponse(dp, request);

          let thresholdPM25Max;
          if (getThresholds) {
            const respAlertDef = await store?.dispatch(actions.getAlertDefinition(patientId, 'air_quality'));
            if (respAlertDef.type === ALERT_DEFINITION_GET_RESULT && respAlertDef.response) {
              thresholdPM25Max = respAlertDef.response.conditions?.find(c => c.field === VITALS.PM25)?.max;
            }
          }

          if (resp.type === PLOT_DATA_RESULT && resp.response.data.length !== 0) {
            const dataPM25 = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.pm2_5?.value,
              };
            });
            const dataPM1 = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.pm1.value,
              };
            });
            const dataPM10 = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.pm10?.value,
              };
            });
            const dataBarometricPressure = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.barometric_pressure?.value,
              };
            });
            const dataCh2o = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.ch2o?.value,
              };
            });
            const dataCo2 = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.co2?.value,
              };
            });
            const dataLuminocity = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.luminocity?.value,
              };
            });
            const dataNoise = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.noise?.value,
              };
            });
            const dataNox = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.nox_index?.value,
              };
            });
            const dataRelativeHumidity = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.relative_humidity?.value,
              };
            });
            const dataAirTemperature = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.temperature?.value,
              };
            });
            const dataVoc = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.voc_index?.value,
              };
            });

            ret[graphMetricEnum.pm25] = {
              data: prepareSimplifiedDataForDataProvider(dataPM25),
              thresholdMax: thresholdPM25Max,
              badData: v => v > thresholdPM25Max,
            };
            ret[graphMetricEnum.pm1] = {
              data: prepareSimplifiedDataForDataProvider(dataPM1),
            };
            ret[graphMetricEnum.pm10] = {
              data: prepareSimplifiedDataForDataProvider(dataPM10),
            };
            ret[graphMetricEnum.barometricPressure] = {
              data: prepareSimplifiedDataForDataProvider(dataBarometricPressure),
            };
            ret[graphMetricEnum.ch2o] = {
              data: prepareSimplifiedDataForDataProvider(dataCh2o),
            };
            ret[graphMetricEnum.co2] = {
              data: prepareSimplifiedDataForDataProvider(dataCo2),
            };
            ret[graphMetricEnum.luminocity] = {
              data: prepareSimplifiedDataForDataProvider(dataLuminocity),
            };
            ret[graphMetricEnum.noise] = {
              data: prepareSimplifiedDataForDataProvider(dataNoise),
            };
            ret[graphMetricEnum.nox] = {
              data: prepareSimplifiedDataForDataProvider(dataNox),
            };
            ret[graphMetricEnum.relativeHumidity] = {
              data: prepareSimplifiedDataForDataProvider(dataRelativeHumidity),
            };
            ret[graphMetricEnum.airTemperature] = {
              data: prepareSimplifiedDataForDataProvider(dataAirTemperature),
            };
            ret[graphMetricEnum.voc] = {
              data: prepareSimplifiedDataForDataProvider(dataVoc),
            };
          }
        }
        break;

      case graphDataProvidersEnum.ptInr:
        {
          resp = await getPlotResponse(dp, request);

          let thresholdPtMin, thresholdPtMax, thresholdInrMin, thresholdInrMax;
          if (getThresholds) {
            const respAlertDef = await store?.dispatch(actions.getAlertDefinition(patientId, 'coagulation'));
            if (respAlertDef.type === ALERT_DEFINITION_GET_RESULT && respAlertDef.response) {
              thresholdPtMin = respAlertDef.response.conditions?.find(c => c.field === VITALS.PROTHROMBIN_TIME)?.min;
              thresholdPtMax = respAlertDef.response.conditions?.find(c => c.field === VITALS.PROTHROMBIN_TIME)?.max;
              thresholdInrMin = respAlertDef.response.conditions?.find(c => c.field === VITALS.INTERNATIONAL_NORMALIZED_RATIO)?.min;
              thresholdInrMax = respAlertDef.response.conditions?.find(c => c.field === VITALS.INTERNATIONAL_NORMALIZED_RATIO)?.max;
            }
          }

          if (resp.type === PLOT_DATA_RESULT && resp.response.data.length !== 0) {
            const dataPt = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.pt_value,
              };
            });
            ret[graphMetricEnum.pt] = {
              data: dataPt.filter(d => !isNaN(d.value)),
              thresholdMin: thresholdPtMin,
              thresholdMax: thresholdPtMax,
              badData: v => v < thresholdPtMin || v > thresholdPtMax,
            };

            const dataInr = resp.response.data.map(i => {
              return {
                dt: moment(i.timestamp * 1000),
                backend_timestamp: i.backend_timestamp,
                value: i.inr_value,
              };
            });
            ret[graphMetricEnum.inr] = {
              data: dataInr.filter(d => !isNaN(d.value)),
              thresholdMin: thresholdInrMin,
              thresholdMax: thresholdInrMax,
              badData: v => v < thresholdInrMin || v > thresholdInrMax,
            };
          }
        }
        break;

      case graphDataProvidersEnum.spirometer:
        {
          resp = await getPlotResponse(dp, request);

          let thresholdFev1Min, thresholdFev1Max, thresholdFvcMin, thresholdFvcMax;
          if (getThresholds) {
            const respAlertDef = await store?.dispatch(actions.getAlertDefinition(patientId, 'spirometer'));
            if (respAlertDef.type === ALERT_DEFINITION_GET_RESULT && respAlertDef.response) {
              thresholdFev1Min = respAlertDef.response.conditions?.find(c => c.field === VITALS.FORCED_EXPIRATORY_VOLUME_1S)?.min;
              thresholdFev1Max = respAlertDef.response.conditions?.find(c => c.field === VITALS.FORCED_EXPIRATORY_VOLUME_1S)?.max;
              thresholdFvcMin = respAlertDef.response.conditions?.find(c => c.field === VITALS.FORCED_VITAL_CAPACITY)?.min;
              thresholdFvcMax = respAlertDef.response.conditions?.find(c => c.field === VITALS.FORCED_VITAL_CAPACITY)?.max;
            }
          }

          if (resp.type === PLOT_DATA_RESULT && resp.response.data.length !== 0) {
            const dataFev1 = resp.response.data.map(i => {
              return {
                dt: moment(i.eventTimestamp),
                backend_timestamp: i.backend_timestamp,
                value: i.serie.fev1,
              };
            });
            ret[graphMetricEnum.fev1] = {
              data: dataFev1.filter(d => !isNaN(d.value)),
              thresholdMin: thresholdFev1Min,
              thresholdMax: thresholdFev1Max,
              badData: v => v < thresholdFev1Min || v > thresholdFev1Max,
            };

            const dataFvc = resp.response.data.map(i => {
              return {
                dt: moment(i.eventTimestamp),
                backend_timestamp: i.backend_timestamp,
                value: i.serie.fvc,
              };
            });
            ret[graphMetricEnum.fvc] = {
              data: dataFvc.filter(d => !isNaN(d.value)),
              thresholdMin: thresholdFvcMin,
              thresholdMax: thresholdFvcMax,
              badData: v => v < thresholdFvcMin || v > thresholdFvcMax,
            };

            const dataSpirometrySeries = resp.response.data.map(i => {
              return {
                dt: moment(i.eventTimestamp),
                backend_timestamp: i.backend_timestamp,
                value: i.serie,
              };
            });
            ret['spirometrySeries'] = { data: dataSpirometrySeries };
          }
        }
        break;

      default:
        break;
    }
    Object.keys(ret).forEach(key => {
      ret[key] = {
        ...ret[key],
        firstEvent: dtStart,
        lastEvent: dtEnd,
      };
    });
    return ret;
  }

  static getEmptyData(dp) {
    const ret = {};
    graphMetrics
      .filter(m => m.dataProvider === dp)
      .forEach(m => {
        ret[m.id] = { data: [] };
      });
    return ret;
  }

  static async getDateRange(dp, patientId, deviceId, hubId, kitId) {
    const request = { dataType: getRequestDataType(dp) };
    if (patientId) request.patientId = patientId;
    if (deviceId) request.deviceId = deviceId;
    if (hubId) request.hubId = hubId;
    if (kitId) request.kitId = kitId;

    const resp = await store?.dispatch(plotActions.getPlotDateRange(request));

    if (resp.type === PLOT_DATE_RANGE_RESULT && resp.response.length !== 0 && resp.response.first_event && resp.response.last_event) {
      return {
        startDate: momentRoundToFullSecond(resp.response.first_event, false),
        endDate: momentRoundToFullSecond(resp.response.last_event),
      };
    }

    return {
      startDate: null,
      endDate: null,
    };
  }

  static async getAnnotations(patientId, dtStart, dtEnd) {
    const request = {
      startDate: dtStart.format(),
      endDate: dtEnd.format(),
      patientId,
    };

    const resp = await store?.dispatch(patientActions.getAnnotations(patientId, request));

    if (resp.type === GET_ANNOTATIONS_RESULT && resp.response?.annotations.length !== 0) {
      return resp.response.annotations.map(i => {
        let metrics = [];
        i.vital_types?.forEach(v => {
          metrics = metrics.concat(getMetricsByReadings(v));
        });
        return {
          ...i,
          dt: moment(i.datetime),
          label: i.text,
          metrics: metrics.filter(r => !!r),
        };
      });
    }

    return [];
  }
}

const momentRoundToFullSecond = (dt, up = true) => {
  if (!dt) {
    return dt;
  }
  const date = moment(dt);
  if (!up) {
    return date.clone().startOf('second');
  }
  if (date.millisecond() === 0) {
    return date;
  }
  return date.clone().add(1, 'seconds').startOf('second');
};